import React from 'react';
import { Card, Radio, Typography } from 'antd';
import { QuoteLineItemProps, PricesByQuantity } from '@partsbadger/types';
import { Price } from '@partsbadger/library';

export interface LeadTimeOptionCardProps {
    option: PricesByQuantity;
    readonly: boolean;
    lineItem: QuoteLineItemProps;
    isDisabled: boolean;
    loading?: boolean;
    isSelected?: boolean;
}

const CARD_HEIGHT_PX = 72;
const BADGE_GAP_BOTTOM = 12;

export const LeadTimeOptionCard: React.FC<LeadTimeOptionCardProps> = ({
    option,
    readonly,
    lineItem,
    isDisabled,
    loading = false,
    isSelected = false,
}) => {
    const isExpedited = option?.type === 'Expedited';

    return (
        <Card
            data-testid="lead-time-card"
            size="small"
            loading={loading}
            style={{
                width: '100%',
                minWidth: 200,
                maxWidth: 240,
                height: CARD_HEIGHT_PX,
                maxHeight: CARD_HEIGHT_PX,
                marginBottom: 6,
                marginRight: 6,
                border: isSelected ? '2px solid #2094f3' : '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                cursor: readonly ? 'default' : 'pointer',
                backgroundColor: isDisabled ? '#f5f5f5' : isSelected ? '#f8fbff' : '#fff',
                opacity: isDisabled ? 0.6 : 1,
                transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
            bodyStyle={{
                padding: '8px 12px',
                height: CARD_HEIGHT_PX,
                minHeight: CARD_HEIGHT_PX,
                maxHeight: CARD_HEIGHT_PX,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    flex: 1,
                    minHeight: 0,
                    paddingBottom: BADGE_GAP_BOTTOM,
                }}
            >
                {readonly ? (
                    <>
                        <Typography.Text strong style={{ flex: 1, minWidth: 0 }} ellipsis>
                            {option?.lead_time} Business Days
                        </Typography.Text>
                        <PriceBlock option={option} isSelected={isSelected} />
                    </>
                ) : (
                    <Radio value={option?.id} style={{ width: '100%', height: '100%', margin: 0 }}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            <Typography.Text strong style={{ flex: 1, minWidth: 0 }} ellipsis>
                                {option?.lead_time} Business Days
                            </Typography.Text>
                            <PriceBlock option={option} isSelected={isSelected} />
                        </div>
                    </Radio>
                )}
            </div>

            <div
                style={{
                    position: 'absolute',
                    bottom: 6,
                    left: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 9999,
                    color: '#fff',
                    background: isExpedited ? '#1890ff' : '#52c41a',
                }}
            >
                {isExpedited ? 'Expedited' : 'Standard'}
            </div>
        </Card>
    );
};

function PriceBlock({
    option,
    isSelected,
}: {
    option: PricesByQuantity;
    isSelected: boolean;
}) {
    return (
        <div data-testid="price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <Price value={option?.unit_price} /> ea.
            </Typography.Text>
            <Typography.Text strong style={{ fontSize: 16, color: isSelected ? '#c00' : undefined }}>
                <Price value={option?.total_price} />
            </Typography.Text>
        </div>
    );
}
